import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeapParticipationConsent } from "../../../store/slices/leapParticipationConsentSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "");
const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
const sigSrc = (v) => (!v ? null : v.startsWith("data:") ? v : `${STORAGE_URL}${v}`);

export default function LeapParticipationConsentPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentLeapParticipationConsent, isLoading, error } = useSelector((s) => s.leapParticipationConsent);

  useEffect(() => {
    if (id) dispatch(fetchLeapParticipationConsent(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentLeapParticipationConsent) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => { clearTimeout(t); window.onafterprint = null; };
    }
  }, [currentLeapParticipationConsent, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading LEAP Participation Consent print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentLeapParticipationConsent) return null;

  const d = currentLeapParticipationConsent;

  const SignLine = ({ label, name, sig, date }) => (
    <div className="sign-line">
      <span className="sign-label">{label} Name:</span> <span className="sign-name">{name || "___________________________"}</span>
      &nbsp;&nbsp;Signature: {sigSrc(sig) ? <img src={sigSrc(sig)} alt={`${label} signature`} className="sign-img" /> : <span>_________________________________</span>}
      &nbsp;&nbsp;Date: <span>{date ? fmt(date) : "_____________________"}</span>
    </div>
  );

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 16mm 14mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .section-title { border-bottom: 1px solid #000 !important; }
        }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .leap-print, .leap-print * { font-family: "Times New Roman", Times, serif; color: #000; }
        .leap-print { width: 100%; max-width: 800px; margin: 0 auto; padding: 18px; font-size: 11pt; line-height: 1.45; }
        .header-logo { text-align: center; margin-bottom: 6px; }
        .header-logo img { height: 62px; }
        .doc-title { text-align: center; font-size: 16pt; font-weight: bold; text-transform: uppercase; margin: 0; }
        .doc-sub { text-align: center; font-size: 10pt; font-style: italic; margin: 2px 0 10px; }
        .meta-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 12px; margin-bottom: 12px; font-size: 10pt; }
        .lbl { font-weight: bold; }
        .section { margin-bottom: 12px; }
        .section-title { font-weight: bold; border-bottom: 1px solid #000; margin-bottom: 4px; padding-bottom: 2px; }
        .sign-block { margin-top: 18px; }
        .sign-line { margin-bottom: 8px; font-size: 10pt; }
        .sign-label { font-weight: bold; }
        .sign-img { height: 32px; vertical-align: middle; }
        .footer { text-align: center; font-size: 8.5pt; margin-top: 16px; border-top: 1px solid #000; padding-top: 6px; }
      `}</style>

      <PrintWrapper>
        <div className="leap-print">
          <div className="header-logo"><img src={logo} alt="CoLPT Logo" /></div>
          <h1 className="doc-title">LEAP Participation Consent Form</h1>
          <div className="doc-sub">Lived Experience Advisory Panel (LEAP) — Caravan of Life Trust Pakistan (CoLPT)</div>

          <div className="section">
            <div className="section-title">Purpose</div>
            <p>LEAP is an advisory panel of individuals with lived experience who provide feedback and guidance to improve CoLPT's mental health services, programs, and policies.</p>
          </div>

          <div className="section">
            <div className="section-title">Voluntary Participation</div>
            <p>I understand that:</p>
            <ul>
              <li>My participation is voluntary.</li>
              <li>I may withdraw at any time without penalty.</li>
              <li>My services at CoLPT (including TRH) will not be affected if I choose not to participate or withdraw.</li>
            </ul>
          </div>

          <div className="section">
            <div className="section-title">Responsibilities</div>
            <p>As a LEAP member, I agree to:</p>
            <ul>
              <li>Attend meetings regularly and inform staff if I cannot attend.</li>
              <li>Participate respectfully and constructively.</li>
              <li>Maintain confidentiality of discussions and members.</li>
              <li>Follow ethical guidelines and declare any conflicts of interest.</li>
            </ul>
            <p><em>I understand that continued participation depends on responsible and respectful involvement.</em></p>
          </div>

          <div className="section">
            <div className="section-title">Use of My Contributions</div>
            <ul>
              <li>My insights and feedback may be used to improve services, programs, research, and policies.</li>
              <li>My lived experience may be shared in reports or presentations in a non-identifiable way.</li>
              <li>My name or personal story will not be used publicly without my written permission.</li>
            </ul>
          </div>

          <div className="section">
            <div className="section-title">Data Protection</div>
            <ul>
              <li>My personal information will be kept confidential and stored securely by CoLPT.</li>
              <li>I will not have access to confidential client records unless officially authorized.</li>
              <li>Shared materials are for advisory purposes only and will not be distributed without permission.</li>
            </ul>
          </div>

          <div className="section">
            <div className="section-title">Emotional Safety</div>
            <p>I understand that discussions may be sensitive. I may pause, step out, or decline to share at any time.</p>
          </div>

          <div className="section">
            <div className="section-title">Agreement</div>
            <p>{d.agreement_confirmed ? "☑" : "☐"} By signing below, I confirm that I understand my role and agree to participate in LEAP.</p>
          </div>

          <div className="sign-block">
            <SignLine label="Participant" name={d.participant_name} sig={d.participant_signature} date={d.participant_sign_date} />
            <SignLine label="Staff" name={d.staff_name} sig={d.staff_signature} date={d.staff_sign_date} />
          </div>

          <div className="footer">CONFIDENTIAL — For Authorised Clinical Use Only</div>
        </div>
      </PrintWrapper>
    </>
  );
}
